import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { SelectedChoices } from '@grandvisionhq/common';
import { useIntl } from '@grandvisionhq/state';
import { isValidStepKey, bookAppointmentSelectedChoiceClickEvent } from '../../data-layer/actions.js';
import { useAppointmentSlots } from '../../pages/BookAppointment/context/appointment-slots.js';
import { useAppointmentContext } from '../../pages/BookAppointment/context/AppointmentContext.js';
import { useRoutingContext } from '../../pages/BookAppointment/context/RoutingContext/context.js';
import '@grandvisionhq/graphql';
import '@grandvisionhq/utils';
import '../../pages/BookAppointment/queries/get-book-appointment-store.js';
import '../../pages/BookAppointment/queries/get-book-appointment-store-by-id.js';
import '../../pages/BookAppointment/queries/get-book-appointment-type.js';
import useVirtualAppointment from '../../pages/BookAppointment/hooks/use-virtual-appointment.js';

const SelectedChoicesContainer = (props)=>{
    const { getLabel , dateTime: { formatLongDate , formatTime  } ,  } = useIntl();
    const [{ setStore , setType , setTimeSlot  }, { store , type , timeSlot  }] = useAppointmentContext();
    const [{ isVirtualAppointmentStoreById , isVirtualAppointmentType  }] = useVirtualAppointment();
    const [{ goTo  }, { currentRoute , queryParams  }] = useRoutingContext();
    const { Slots  } = useAppointmentSlots();
    const onNavigate = React.useCallback((targetRoute)=>{
        const dataLayerSteps = {
            '': 1,
            where: 1,
            what: 2,
            when: 3,
            who: 4
        };
        if (isValidStepKey(currentRoute, dataLayerSteps) && isValidStepKey(targetRoute, dataLayerSteps)) {
            const dataLayerEvent = bookAppointmentSelectedChoiceClickEvent({
                store: store && !isVirtualAppointmentType(type) ? {
                    code: store.code,
                    globalStoreId: store.globalStoreId,
                    streetName: store.streetName,
                    town: store.town,
                    name: store.name,
                    postalCode: store.postalCode,
                    streetNumber: store.streetNumber,
                    additionalStreetInfo: store.additionalStreetInfo
                } : undefined,
                appointment: type ? {
                    appointmentType: {
                        id: type.id,
                        ageGroup: type.ageGroup,
                        isVirtualAppointment: isVirtualAppointmentType(type)
                    },
                    startTime: timeSlot === null || timeSlot === void 0 ? void 0 : timeSlot.from,
                    endTime: timeSlot === null || timeSlot === void 0 ? void 0 : timeSlot.until
                } : undefined
            }, dataLayerSteps, currentRoute, targetRoute);
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        currentRoute,
        isVirtualAppointmentType,
        store,
        type,
        timeSlot
    ]);
    const choices = React.useMemo(()=>{
        const value = {};
        const changeLabel = getLabel('appointments.component.selectedChoices.change');
        const clearLabel = getLabel('appointments.component.selectedChoices.clear');
        if (store && !isVirtualAppointmentStoreById(store)) {
            value.store = {
                name: store.shortName,
                action: changeLabel,
                onClick: ()=>{
                    const nextStep = 'where';
                    const { typeId , search , userLocation  } = queryParams;
                    onNavigate(nextStep);
                    setStore(null);
                    goTo(nextStep, {
                        typeId,
                        search,
                        userLocation
                    });
                }
            };
        }
        if (type) {
            const typeValue = {
                name: type.name,
                action: changeLabel
            };
            const { storeId , search , userLocation  } = queryParams;
            if (store) {
                typeValue.onClick = ()=>{
                    const nextStep = isVirtualAppointmentStoreById(store) ? 'where' : 'what';
                    onNavigate(nextStep);
                    setTimeSlot(null);
                    setType(null);
                    if (nextStep === 'where') {
                        setStore(null);
                        goTo(nextStep, {
                            search,
                            userLocation
                        });
                        return;
                    }
                    goTo(nextStep, {
                        storeId,
                        search,
                        userLocation
                    });
                };
            } else {
                typeValue.action = clearLabel;
                typeValue.onClick = ()=>{
                    const nextStep = 'where';
                    onNavigate(nextStep);
                    setType(null);
                    goTo(nextStep, {
                        search,
                        userLocation
                    });
                };
            }
            value.type = typeValue;
        }
        if (timeSlot) {
            const name = `${formatLongDate(timeSlot.from)}, ${formatTime(timeSlot.from)}`;
            value.timeSlot = {
                name,
                action: changeLabel,
                onClick: ()=>{
                    const nextStep = 'when';
                    const { storeId , typeId , search , userLocation  } = queryParams;
                    onNavigate(nextStep);
                    setTimeSlot(null);
                    goTo(nextStep, {
                        storeId,
                        typeId,
                        search,
                        userLocation
                    });
                }
            };
        }
        if (Slots.createSelectedChoices) {
            // Returns a custom array of selected choices that's passed in the slots provider
            return Slots.createSelectedChoices(value);
        }
        return value;
    }, [
        getLabel,
        store,
        isVirtualAppointmentStoreById,
        type,
        timeSlot,
        Slots,
        queryParams,
        onNavigate,
        setStore,
        goTo,
        setTimeSlot,
        setType,
        formatLongDate,
        formatTime, 
    ]);
    return /*#__PURE__*/ React.createElement(SelectedChoices, swcHelpers.extends({}, props, {
        choices: choices
    }));
};

export { SelectedChoicesContainer as default };
//# sourceMappingURL=SelectedChoicesContainer.js.map
